import { useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import {
  LakehouseConnectionTypeEnum,
  LakehouseSnowflakeConnectionInput,
  SnowflakeAuthType,
} from '@vendia/management-api-types'
import debug from 'debug'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import Card from 'src/components/containers/card'
import ListboxField from 'src/components/fields/listbox.field'
import TextField from 'src/components/fields/text.field'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { createOnChangeValidator, isRequiredValidator } from 'src/utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useFirstFocus } from 'src/utils/hooks/use-first-focus'
import { getSnowflakeOAuthInfo, setupOAuthMessageListener } from 'src/utils/lakehouse/connection'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from 'src/utils/lakehouse/validators'
import notify from 'src/utils/notify'

import { StepId } from '../config'
import { GetLakehouseTablesDocument } from '@vendia/control-plane-api'

const logger = debug('app:Snowflake')

export const StepConnectSnowflake: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const form = context.form
  const client = useControlPlaneApi()
  const navigate = useNavigate()
  const prefix = 'source.snowflake'
  const nameRef = useFirstFocus()

  const { uniName, nodeName } = useStore(form.store, (s) => s.values)
  const connection = useStore(form.store, (s) => s.values.source.connection)

  const snowflake = {
    ...useStore(form.store, (s) => s.values.source?.snowflake),
    authType: useStore(form.store, (s) => s.values.source?.snowflake?.authType) || SnowflakeAuthType.Password,
  }

  const getTablesMutation = useMutation({
    mutationFn: () =>
      client.graphqlFetch({
        query: GetLakehouseTablesDocument,
        variables: {
          uniName: uniName!,
          nodeName: nodeName!,
          connectionInput: {
            type: LakehouseConnectionTypeEnum.Snowflake,
            snowflakeConnection: {
              ...(snowflake as LakehouseSnowflakeConnectionInput),
            },
          },
        },
      }),
    onError: () => notify.error('Error validating Snowflake connection. Please check your connection details.'),
    onSuccess: (response) => {
      logger('snowflake connection response', response)
      if (response.errors) {
        notify.error('Error validating Snowflake connection. Please check your connection details.')
        return
      }
      if (!response.getLakehouseTables) {
        notify.error('Error testing Snowflake connection')
        return
      }
      form.setFieldValue('source.availableTables', response.getLakehouseTables.tables as string[])
      form.setFieldValue('source.sourceRegion', response.getLakehouseTables.region as string)
      context.goToStep(StepId.SelectTable)
    },
  })

  const { authorizeUrl, setupListener } = getSnowflakeOAuthInfo({
    snowflake: {
      ...(snowflake as LakehouseSnowflakeConnectionInput),
    },
    form: form as any,
    uniName: uniName!,
    nodeName: nodeName!,
    prefix: `${prefix}.oauth`,
    api: client,
    onSuccess: () => getTablesMutation.mutate(),
  })

  useEffect(() => {
    const cleanup = setupListener()
    return cleanup
  }, [snowflake])

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Connect to Snowflake'
        description={'Let’s authenticate your Snowflake account'}
      />
      <ScrollableStepContent inset autoHeight>
        <Card padding='xl' className='mx-8 w-full max-w-4xl' childrenWrapperClassName='flex w-full flex-col gap-4'>
          <TextField
            ref={nameRef}
            name={`connectionName`}
            label='Name'
            description='Easily identify and reuse this connection to ingest additional tables.'
            form={form}
            maxLength={64}
            useNestedLabel
            validators={createOnChangeValidator(
              {
                pattern: /^(?!\s*$).{1,64}$/,
                message: 'The connection name must be between 1 and 64 characters long.',
              },
              true,
            )}
          />
          <TextField
            name={`${prefix}.account`}
            label='Snowflake Account Identifier'
            description={
              <span>
                The first part of your Snowflake account&apos;s URL, eg.
                &lt;account-identifier&gt;.snowflakecomputing.com.{' '}
                <a
                  target='_blank'
                  className='text-link font-bold'
                  href='https://docs.snowflake.com/en/user-guide/admin-account-identifier'
                  rel='noreferrer'
                >
                  Learn more
                </a>
              </span>
            }
            className='!w-max flex-1 !rounded-none !border-l-0 !border-r-0'
            leftOfInput={
              <div className='text-light flex h-12 select-none items-center truncate rounded-md rounded-r-none bg-slate-100 px-4 shadow ring-1 ring-slate-300'>
                https://
              </div>
            }
            rightOfInput={
              <div className='text-light flex h-12 select-none items-center truncate rounded-md rounded-l-none bg-slate-100 px-4 shadow ring-1 ring-slate-300'>
                .snowflakecomputing.com
              </div>
            }
            placeholder='ABC123.us-east-1 or ORG123.ABC123'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectSnowflake,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Snowflake),
            })}
          />

          <TextField
            name={`${prefix}.warehouse`}
            label='Warehouse'
            description='The warehouse that should be used to execute queries'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectSnowflake,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Snowflake),
            })}
          />

          <TextField
            name={`${prefix}.database`}
            label='Database'
            description='The database containing the tables you want to share'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectSnowflake,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Snowflake),
            })}
          />
          <ListboxField
            name={`${prefix}.authType`}
            label='Authentication type'
            description='The authentication type used to access your Snowflake account'
            options={[
              { value: SnowflakeAuthType.Password, label: 'Username/Password' },
              { value: SnowflakeAuthType.Oauth, label: 'OAuth 2.0' },
            ]}
            defaultValue={form.state.values.source?.snowflake?.authType || SnowflakeAuthType.Password}
            form={form}
            useNestedLabel
            onChange={(value) => {
              if (value === SnowflakeAuthType.Password) {
                form.deleteField(`${prefix}.oauth.clientId`)
                form.deleteField(`${prefix}.oauth.clientSecret`)
              } else if (value === SnowflakeAuthType.Oauth) {
                form.deleteField(`${prefix}.username`)
                form.deleteField(`${prefix}.password`)
              }
            }}
          />

          {snowflake.authType === 'PASSWORD' && (
            <>
              <TextField
                name={`${prefix}.username`}
                label='Username'
                description='The username for the Snowflake account you want to connect with'
                form={form}
                useNestedLabel
                validators={lakehouseFlowFieldValidator({
                  stepId: StepId.ConnectSnowflake,
                  listenTo: ['source.connection', `${prefix}.authType`],
                  validator: isRequiredValidator(
                    null,
                    connection === LakehouseConnectionTypeEnum.Snowflake && snowflake.authType === 'PASSWORD',
                  ),
                })}
              />
              <TextField
                name={`${prefix}.password`}
                label='Password'
                description='The password for the Snowflake account you want to connect with'
                form={form}
                type='password'
                useNestedLabel
                validators={lakehouseFlowFieldValidator({
                  stepId: StepId.ConnectSnowflake,
                  listenTo: ['source.connection', `${prefix}.authType`],
                  validator: isRequiredValidator(
                    null,
                    connection === LakehouseConnectionTypeEnum.Snowflake && snowflake.authType === 'PASSWORD',
                  ),
                })}
              />
            </>
          )}
          {snowflake.authType === 'OAUTH' && (
            <>
              <TextField
                name={`${prefix}.oauth.clientId`}
                label='Client ID'
                description='The client ID for the Snowflake account you want to connect with'
                form={form}
                useNestedLabel
                validators={lakehouseFlowFieldValidator({
                  stepId: StepId.ConnectSnowflake,
                  listenTo: ['source.connection', `${prefix}.authType`],
                  validator: isRequiredValidator(
                    null,
                    connection === LakehouseConnectionTypeEnum.Snowflake && snowflake.authType === 'OAUTH',
                  ),
                })}
              />
              <TextField
                name={`${prefix}.oauth.clientSecret`}
                label='Client Secret'
                description='The client secret for the Snowflake account you want to connect with'
                form={form}
                type='password'
                useNestedLabel
                validators={lakehouseFlowFieldValidator({
                  stepId: StepId.ConnectSnowflake,
                  listenTo: ['source.connection', `${prefix}.authType`],
                  validator: isRequiredValidator(
                    null,
                    connection === LakehouseConnectionTypeEnum.Snowflake && snowflake.authType === 'OAUTH',
                  ),
                })}
              />
            </>
          )}

          <TextField
            name={`${prefix}.schema`}
            label='Schema'
            description='The schema where your tables are located. "PUBLIC" schema will be used by default'
            type='text'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectSnowflake,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Snowflake),
            })}
          />

          <TextField
            name={`${prefix}.role`}
            label='Role'
            description="The name of the role to use when connecting to Snowflake. If not provided, the user's default role will be used."
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectSnowflake,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Snowflake),
            })}
          />
        </Card>
      </ScrollableStepContent>
      <form.Subscribe selector={(state) => [state.canSubmit, state.isPristine]}>
        {([canSubmit, isPristine]) => (
          <StepButtons
            context={context}
            isSubmitting={getTablesMutation.isPending}
            nextBlocked={getTablesMutation.isPending || !canSubmit || isPristine}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            onNext={async () => {
              const fieldErrors = await form.validateAllFields('change')
              if (fieldErrors.length > 0) {
                return { fieldErrors, formErrors: {}, hasErrors: true, isValid: false }
              }
              if (snowflake.authType === SnowflakeAuthType.Oauth) {
                const hasOAuthTokens =
                  !!snowflake?.oauth?.accessToken || !!form.state.values.source?.snowflake?.oauth?.accessToken

                if (hasOAuthTokens) {
                  getTablesMutation.mutate()
                } else {
                  window.open(authorizeUrl, '_blank')
                }
              } else if (snowflake.authType === SnowflakeAuthType.Password) {
                getTablesMutation.mutate()
              }
            }}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}
